import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react'
import get from 'lodash/get'

import { BlockModel, NULL as BlockModelNULL } from './model'
import { BLOCK_TYPES } from './constants'
import ColumnModel, {
  NULL as ColumnModelNULL,
} from '@client/components/DroppableWrapper/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import RenderBlockByType from './RenderBlockByType'
import CustomEvent from '@client/components/CustomEvent'
import Toolbar from '@client/components/Toolbar'
import inlineStyles from './inlineStyles'
import {
  ITEM_TYPE_FORM,
  ITEM_TYPE_LANDING_PAGE,
  ITEM_TYPE_LANDING_PAGE_SUCCESS,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_SURVEY,
  ITEM_TYPE_SURVEY_THANK_YOU,
  WIZARD_TYPE_PAGE,
} from '@client/components/Editor/settings'
import { cancelStorage } from '@client/utils/Storage'
import { SECTION_TYPES } from '@client/components/Section/constants'

import './styles.scss'

export const getPadding = ({ kind, section, columnIndex, fullWidth }) => {
  let padding = '0px 20px 0px 20px'

  const sectionType = get(section, 'sectionType')
  if (
    fullWidth && 
    sectionType !== SECTION_TYPES.PRODUCTSTHREECOL && 
    sectionType !== SECTION_TYPES.PRODUCTSTWOCOL
  ) {
    return '0'
  }

  const sidePadding = get(section, 'settings.values.sidePadding') || 0
  const middlePadding = get(section, 'settings.values.middlePadding') || 0
  const isBoxed = !!get(section, 'isBoxed')

  if (sectionType === SECTION_TYPES.ONECOL || isBoxed) {
    padding = `0px ${sidePadding}px 0px ${sidePadding}px`
  } else if (
    (sectionType === SECTION_TYPES.TWOCOL || sectionType === SECTION_TYPES.THREECOL) &&
    columnIndex === 0 &&
    !isBoxed
  ) {
    padding = `0px ${Math.ceil(middlePadding / 2)}px 0px ${sidePadding}px`
  } else if (
    (
      (sectionType === SECTION_TYPES.TWOCOL && columnIndex === 1) ||
      (sectionType === SECTION_TYPES.THREECOL && columnIndex === 2)
    ) &&
    !isBoxed
  ) {
    padding = `0px ${sidePadding}px 0px ${Math.ceil(middlePadding / 2)}px`
  } else if (
    sectionType === SECTION_TYPES.THREECOL &&
    columnIndex === 1 &&
    !isBoxed
  ) {
    padding = `0px ${Math.ceil(middlePadding / 2)}px 0px ${Math.ceil(middlePadding / 2)}px`
  }

  if (
    (sectionType === SECTION_TYPES.PRODUCTSTHREECOL) &&
    columnIndex === 0 &&
    !isBoxed
  ) {
    padding = `${sidePadding}px ${
      Math.ceil(sidePadding / 3)
    }px ${
      sidePadding > 10 ? sidePadding - 10 : 0
    }px ${sidePadding}px`
  } else if (
    (sectionType === SECTION_TYPES.PRODUCTSTHREECOL && columnIndex === 2)
    &&
    !isBoxed
  ) {
    padding = `${sidePadding}px ${sidePadding}px ${
      sidePadding > 10 ? sidePadding - 10 : 0
    }px ${Math.ceil(sidePadding / 3)}px`
  } else if (
    sectionType === SECTION_TYPES.PRODUCTSTHREECOL &&
    columnIndex === 1 &&
    !isBoxed
  ) {
    padding = `${sidePadding}px ${
      Math.ceil(sidePadding * 2 / 3)
    }px ${
      sidePadding > 10 ? sidePadding - 10 : 0
    }px ${
      isOdd(sidePadding) 
        ? Math.floor(sidePadding * 2 / 3) 
        : Math.ceil(sidePadding * 2 / 3)
    }px`
  }

  if (
    (sectionType === SECTION_TYPES.PRODUCTSTWOCOL) &&
    columnIndex === 0 &&
    !isBoxed
  ) {
    padding = `${sidePadding}px ${
      Math.ceil(sidePadding / 2)
    }px ${
      sidePadding > 10 ? sidePadding - 10 : 0
    }px ${sidePadding}px`
  } else if (
    (sectionType === SECTION_TYPES.PRODUCTSTWOCOL && columnIndex === 1)
    &&
    !isBoxed
  ) {
    padding = `${sidePadding}px ${sidePadding}px ${
      sidePadding > 10 ? sidePadding - 10 : 0
    }px ${Math.ceil(sidePadding / 2)}px`
  }

  return padding
}

const isOdd = (num) => Boolean(+num % 2)

export const getPaddingTotal = ({ kind, section, columnIndex, fullWidth }) => {
  const padding = getPadding({ kind, section, columnIndex, fullWidth })

  const totalPadding = padding
    .replace(/px/ig, '')
    .split(' ')
    .map((i) => +i)
    .filter((value, index) => isOdd(index))
    .reduce((result, current) => result + current, 0)

  return totalPadding || 0
}

@observer
class Block extends Component {
  static propTypes = {
    app: PropTypes.instanceOf(AppModel),
    editor: PropTypes.instanceOf(EditorModel),
    component: PropTypes.instanceOf(BlockModel),
    container: PropTypes.instanceOf(ColumnModel),
    index: PropTypes.number,
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    app: AppModelNULL,
    editor: EditorModelNULL,
    component: BlockModelNULL,
    container: ColumnModelNULL,
    index: 0,
    serverRender: false,
  }

  constructor(props) {
    super(props)

    props.component.index = props.index
    props.component.container = props.container
    props.component.columnIndex = props.columnIndex
    props.component.section = props.section
    props.component.kind = props.editor.kind
    this.state = {}
  }

  // Expose DOM element for Draggable component
  getDOMElement = () => {
    return this.dom
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.component && nextProps.component.index !== nextProps.index) {
      nextProps.component.index = nextProps.index
    }
    return null
  }

  getTableCellStyles(model) {
    let padding = getPadding({
      kind: this.props.editor.kind,
      section: this.props.section,
      columnIndex: this.props.columnIndex,
      fullWidth: model.settings.values.fullWidth,
    })

    let fontSize = inlineStyles.block_td.fontSize
    if (this.props.editor.kind === ITEM_TYPE_FORM && this.props.editor.form_type === 'popup') {
      padding = '0 40px'
    }
    if (this.props.editor.kind === ITEM_TYPE_FORM && this.props.editor.form_type === 'slide_in') {
      padding = '0 30px'
    }
    if (this.props.editor.kind === ITEM_TYPE_SURVEY) {
      padding = '0 40px'
      if (
        this.props.section.sectionType === SECTION_TYPES.TWOCOL &&
        this.props.columnIndex === 0
      ) {
        padding = '0 20px 0 40px'
      }
      if (
        this.props.section.sectionType === SECTION_TYPES.TWOCOL &&
        this.props.columnIndex === 1
      ) {
        padding = '0 40px 0 20px'
      }
    }
    if (
      this.props.editor.kind === ITEM_TYPE_LANDING_PAGE ||
      this.props.editor.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS ||
      (
        this.props.editor.kind === ITEM_TYPE_PAGE &&
        this.props.app.wizard.type === WIZARD_TYPE_PAGE
      )
    ) {
      padding = '0'
      fontSize = undefined
    }
    if (
      model.settings && 
      model.settings.values.fullWidth && 
      this.props.section.sectionType !== SECTION_TYPES.PRODUCTSTHREECOL &&
      this.props.section.sectionType !== SECTION_TYPES.PRODUCTSTWOCOL
    ) {
      return {
        ...inlineStyles.block_td_fullwidth,
        fontSize,
      }
    }
    return {
      ...inlineStyles.block_td,
      fontSize,
      padding,
    }
  }

  getTableCellAlign(model) {
    switch (model.type) {
      case BLOCK_TYPES.IMAGE:
      case BLOCK_TYPES.VIDEO:
      case BLOCK_TYPES.BANNER:
        return this.getImageAlign(model)
      case BLOCK_TYPES.BUTTON:
        return this.getButtonAlign(model)
      case BLOCK_TYPES.CONFIRM_BUTTON:
        return this.getButtonAlign(model)
      default:
        return ''
    }
  }

  getImageAlign(model) {
    if (!model.settings) return ''
    const { fullWidth, align } = model.settings.values
    return !fullWidth ? align : ''
  }

  getButtonAlign(model) {
    const globalSettings = this.props.editor.settings.values
    if (!model.settings) return globalSettings.button_align
    const { align } = model.settings.values
    return !!align ? align : globalSettings.button_align
  }

  getFullWidthClassName(model) {
    switch (model.type) {
      case BLOCK_TYPES.IMAGE:
      case BLOCK_TYPES.VIDEO:
        if (
          model.settings && 
          model.settings.values.fullWidth && 
          this.props.section.sectionType !== SECTION_TYPES.PRODUCTSTHREECOL
        ) {
          return ' full-width'
        }
        return ''
      default:
        return ''
    }
  }

  getProductColumnClassName(model) {
    const { component } = this.props
    if (model.type === BLOCK_TYPES.PRODUCT) {
      if (component.columnIndex === 0) {
        return ' gan-product-1'
      }
      if (component.columnIndex === 1) {
        return ' gan-product-2'
      }
      if (component.columnIndex === 2) {
        return ' gan-product-3'
      }
    }
    return ''
  }

  handleMouseOver = (event) => {
    this.props.component.setShowToolbar(
      event.track[0] === event.listenerElement
    )
  }

  handleMouseLeave = (event) => {
    this.props.component.setShowToolbar(
      event.track[0] !== event.listenerElement
    )
  }

  handleEdit = (event) => {
    const { component, editor, app, serverRender } = this.props
    if (!component.showToolbar || serverRender) {
      return
    }
    if (event.target.classList.contains('save')) {
      return
    }
    const item = editor.serialize()
    cancelStorage.saveItem(item)
    app.setEditingSettings(component)
  }

  render() {
    const { component, serverRender, editor } = this.props

    if (
      editor.kind === ITEM_TYPE_FORM ||
      editor.kind === ITEM_TYPE_PAGE ||
      editor.kind === ITEM_TYPE_LANDING_PAGE ||
      editor.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS ||
      editor.kind === ITEM_TYPE_SURVEY ||
      editor.kind === ITEM_TYPE_SURVEY_THANK_YOU
    ) {
      return (
        <div
          className={
            serverRender
              ? undefined
              : `block ${component.showToolbar ? 'show-toolbar' : ''}`
          }
          data-block-id={serverRender ? undefined : component.id}
          onClick={this.handleEdit}
          ref={(el) => (this.dom = el)}
        >
          <CustomEvent
            onMouseOver={this.handleMouseOver}
            onMouseLeave={this.handleMouseLeave}
          >
            <div
              className="gan-popup-row gan-block-parent"
              style={this.getTableCellStyles(component)}
              align={this.getTableCellAlign(component)}
            >
              <Toolbar model={component} />
              <RenderBlockByType component={component} />
            </div>
          </CustomEvent>
        </div>
      )
    }

    return (
      <tr
        className={
          serverRender
            ? undefined
            : `block ${component.showToolbar ? 'show-toolbar' : ''}`
        }
        data-block-id={serverRender ? undefined : component.id}
        onClick={this.handleEdit}
        ref={(el) => (this.dom = el)}
      >
        <CustomEvent
          onMouseOver={this.handleMouseOver}
          onMouseLeave={this.handleMouseLeave}
        >
          <td
            className={
              `gan-block-parent${
                this.getFullWidthClassName(component)
              }${
                this.getProductColumnClassName(component)
              }`
            }
            style={this.getTableCellStyles(component)}
            align={this.getTableCellAlign(component)}
          >
            <Toolbar model={component} />
            <RenderBlockByType component={component} />
          </td>
        </CustomEvent>
      </tr>
    )
  }
}

export default Block
